import { ref } from 'vue';

/**
 * Composable for managing the state of the desktop mega menu.
 */
export function useDesktopMenu() {
  const isDesktopMenuOpen = ref(false);
  const activeDesktopMenu = ref(null); // e.g., 'women', 'men'
  const activeDesktopCategory = ref(null); // e.g., 'women_shoes', 'men_accessories'
  const hoveredDesktopMenu = ref(null); // used for immediate UI feedback
  const hoveredDesktopCategory = ref(null); // used for immediate UI feedback for category hover

  /** Resets all desktop menu states. */
  const resetDesktopMenuState = () => {
    isDesktopMenuOpen.value = false;
    activeDesktopMenu.value = null;
    activeDesktopCategory.value = null;
    hoveredDesktopMenu.value = null;
    hoveredDesktopCategory.value = null;
  };

  /**
   * Sets the currently active category (tab) within an open menu.
   * @param {string | null} categoryId - The ID of the category to activate, or null.
   */
  const setActiveDesktopCategory = (categoryId) => {
    activeDesktopCategory.value = categoryId;
  };

  /**
   * Opens a specific main menu panel.
   * Does not set the default category; that's handled by the component using this composable.
   * @param {string} menuId - The ID of the main menu to open.
   */
  const openDesktopMenu = (menuId) => {
    isDesktopMenuOpen.value = true;
    activeDesktopMenu.value = menuId;
    hoveredDesktopMenu.value = menuId;
  };

  /** Closes any open desktop menu panel. */
  const closeDesktopMenu = () => {
    resetDesktopMenuState();
  };

  /**
   * Sets the currently hovered menu item.
   * @param {string | null} menuId - The ID of the menu being hovered, or null.
   */
  const setHoveredDesktopMenu = (menuId) => {
    hoveredDesktopMenu.value = menuId;
  };

  /**
   * Toggles the desktop menu state based on the provided menuId.
   * - Opens the menu if closed.
   * - Closes the menu if the *same* menuId is clicked again.
   * - Switches to the new menuId if a *different* one is clicked while already open.
   * @param {string} menuId - The ID of the menu being interacted with.
   */
  const toggleDesktopMenu = (menuId) => {
    if (isDesktopMenuOpen.value && activeDesktopMenu.value === menuId) {
      closeDesktopMenu();
    } else if (isDesktopMenuOpen.value) {
      activeDesktopMenu.value = menuId;
      activeDesktopCategory.value = null;
    } else {
      openDesktopMenu(menuId);
    }
  };

  const setHoveredDesktopCategory = (categoryId) => {
    hoveredDesktopCategory.value = categoryId;
  };

  return {
    activeDesktopCategory,
    activeDesktopMenu,
    closeDesktopMenu,
    hoveredDesktopCategory,
    hoveredDesktopMenu,
    isDesktopMenuOpen,
    openDesktopMenu,
    setActiveDesktopCategory,
    setHoveredDesktopCategory,
    setHoveredDesktopMenu,
    toggleDesktopMenu,
  };
}
